import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsList } from '@/components/ScrollableTabsList';
import Loader from '@/components/Loader';
import { useDDIWorkOrderDetail, useDdiInvoice, useDdiSearchResult } from '@/services/dueDiligenceService';
import { DDIWorkOrderSidePanelHeader } from './DDIWorkOrderSidePanelHeader';
import { CaseDetailsDdiContent } from './CaseDetailsDdiContent';
import { SearchDetailsDdiContent } from './SearchDetailsDdiContent';
import { SearchResultsDdiContent } from './SearchResultsDdiContent';
import { BillingDdiContent } from './BillingDdiContent';
import { ReportsDdiContent } from './ReportsDdiContent';

type Props = {
  workOrderId: string;
  onClose: () => void;
};

const tabs = [
  { value: 'CASE_DETAILS', label: 'Case Details' },
  { value: 'SEARCH_DETAILS', label: 'Search Details' },
  { value: 'SEARCH_RESULTS', label: 'Search Results' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'REPORTS', label: 'Reports' },
];

const DDIWorkOrderSidePanel = ({ workOrderId, onClose }: Props) => {
  const [activeTab, setActiveTab] = useState(tabs[0].value);
  const { data: workOrder, isFetching, isError, refetch } = useDDIWorkOrderDetail(workOrderId);
  const {
    data: searchResult,
    isFetching: isSearchResultFetching,
    isError: isSearchResultError,
    refetch: refetchSearchResult,
  } = useDdiSearchResult(workOrderId);
  const {
    data: invoice,
    isFetching: isInvoiceFetching,
    isError: isInvoiceError,
    refetch: refetchInvoice,
  } = useDdiInvoice(workOrderId);

  const tabWidth = useMemo(() => (tabs.length <= 3 ? `${100 / tabs.length}%` : '33.33%'), []);

  return (
    <Sheet modal={false} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white"
        side="right"
        tabIndex={-1}
      >
        <Loader isLoading={isFetching}>
          {isError ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder flex-col gap-4">
              <button
                type="button"
                aria-label="Close side panel"
                className="border-[1px] p-[6px] absolute top-6 right-6 rounded-md cursor-pointer"
                onClick={onClose}
              >
                <X color="#676767" className="h-[20px] w-[20px]" />
              </button>
              <span>FAILED TO LOAD WORK ORDER DETAILS</span>
              <button className="text-primary text-sm underline" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          ) : workOrder ? (
            <Tabs className="w-full text-black" value={activeTab} onValueChange={setActiveTab}>
              <SheetHeader className="flex flex-col gap-4 pb-0 sticky top-0 bg-white">
                <SheetTitle className="flex flex-col gap-4 bg-white pb-0">
                  <DDIWorkOrderSidePanelHeader workOrder={workOrder} invoice={invoice} onClose={onClose} />
                </SheetTitle>
                <ScrollableTabsList className="!mt-0">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      style={{ width: tabWidth }}
                      className="flex-none"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </ScrollableTabsList>
              </SheetHeader>

              <div className="w-full !mt-0 pb-2 text-[#0A0A0A]">
                <TabsContent value="CASE_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <CaseDetailsDdiContent clientDetails={workOrder.clientDetails} />
                </TabsContent>
                <TabsContent value="SEARCH_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <SearchDetailsDdiContent workOrder={workOrder} />
                </TabsContent>
                <TabsContent value="SEARCH_RESULTS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <SearchResultsDdiContent
                    searchResult={searchResult}
                    isLoading={isSearchResultFetching}
                    isError={isSearchResultError}
                    onRetry={() => refetchSearchResult()}
                  />
                </TabsContent>
                <TabsContent value="BILLING" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <BillingDdiContent
                    invoice={invoice}
                    workOrderId={workOrderId}
                    isLoading={isInvoiceFetching}
                    isError={isInvoiceError}
                    onRetry={() => refetchInvoice()}
                    notifyLoadErrors={activeTab === 'BILLING'}
                  />
                </TabsContent>
                <TabsContent value="REPORTS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <ReportsDdiContent />
                </TabsContent>
              </div>
            </Tabs>
          ) : null}
        </Loader>
      </SheetContent>
    </Sheet>
  );
};

export default DDIWorkOrderSidePanel;
